import {
  BASE_DEVIATION_SUBJECT_REFUSALS,
  type BaseDeviationCategory,
  type BaseDeviationLine,
  type BaseDeviationNoteSource,
  type BaseDeviationRunConflict,
  type BaseDeviationSideReadiness,
  type BaseDeviationSubjectRefusal,
  type NotComparedReason,
} from '@clm/shared-types';

import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { refusalCodeOf } from './base-msa-shared';

/**
 * The pure half of the base-MSA deviation report — CLM-112. The contract
 * record's section, the report page and the two offer lines share this copy,
 * so it lives apart from the components (the `base-msa-shared.ts` reason: a
 * component module that also exports functions breaks fast refresh).
 *
 * Role lists are *display hints* (`mayAttempt`, `permissions.ts`); the API's
 * `clause:review_match` check is the gate (R-112-13).
 */

/** The roles the gateway grants `clause:review_match` to — who may run and read a report (D-2.1-11). */
export const BASE_DEVIATION_READER_ROLES: readonly string[] = ['admin', 'legal_reviewer'];

/** The report page's path — under the record, so the breadcrumb reads *Contract record*. */
export function baseDeviationReportPath(contractKey: string, reportId: string): string {
  return `/contracts/record/${encodeURIComponent(contractKey)}/base-deviation/${encodeURIComponent(reportId)}`;
}

/** Paragraph matches, filtered to one contract — the remedy for what could not be compared (R-112-6). */
export function matchReviewPath(contractKey: string): string {
  return `/clauses/match-review?contract_key=${encodeURIComponent(contractKey)}`;
}

/**
 * The offer on the upload success panel and the bulk-ingest rows (R-112-16).
 * At ingest there is no text yet (intake precedes convert, rule 2), and
 * segmentation and matching are operator-dispatched, so the offer says what
 * must happen first and points at the record, where the section lives.
 */
export const BASE_DEVIATION_OFFER_COPY =
  'Once it is converted and its clauses are segmented and matched, compare it with the base MSA from its record.';

/**
 * CLM-110 (§4.7) — the record's section on an MSA whose type no administrator
 * has confirmed yet. The report runs as before; this says what it assumes.
 */
export const BASE_DEVIATION_PROVISIONAL_COPY = 'This contract’s type is provisional; the report assumes it is an MSA.';

/**
 * What a practices-scoped reader with no visible base is told (R-112-12).
 * `scopedWithoutBaseCopy` is **nomination** copy ("this contract becomes the
 * base … version 1") and cannot be reused on a report.
 */
export const NO_VISIBLE_BASE_COPY =
  'No base MSA is visible in your scope, so no report can be run from here. A report compares two contracts, and ' +
  'you must be able to see both — a base outside your scope can be compared only by someone who can see it.';

/** No base exists at all (an all-scope reader), so there is nothing to compare with yet. */
export const NO_BASE_COPY = 'No base MSA has been nominated yet. An administrator nominates one on the Base MSA page.';

/** Each side's readiness, in the page's own words (R-112-10). `null` for `ready`. */
export function readinessCopy(side: 'subject' | 'base', readiness: BaseDeviationSideReadiness): string | null {
  const who = side === 'subject' ? 'This contract' : 'The base MSA';
  switch (readiness) {
    case 'ready':
      return null;
    case 'not_converted':
      return `${who} has no converted text yet. The report compares converted text, so it waits for conversion.`;
    case 'not_segmented':
      return `${who} has not been segmented into clauses yet (the mine-clauses segment stage).`;
    case 'not_matched':
      return `None of ${side === 'subject' ? 'this contract’s' : 'the base MSA’s'} paragraphs has been matched to the clause library yet (match-clauses).`;
    case 'spans_stale':
      return (
        `${who} was re-converted after it was segmented, so every stored paragraph describes text that has moved. ` +
        'It must be re-segmented before it can be compared.'
      );
    default: {
      const exhaustive: never = readiness;
      return String(exhaustive);
    }
  }
}

/** Why a contract cannot be a subject — the section and a 400 on Run share these (R-112-10). */
export const SUBJECT_REFUSAL_COPY: Readonly<Record<BaseDeviationSubjectRefusal, string>> = Object.freeze({
  wrong_contract_type: 'Only an MSA is compared with the base MSA.',
  amendment:
    'This contract amends another agreement and holds only the changes to it. Run the report on the agreement it amends.',
  same_as_base: 'This contract is the base MSA, so there is nothing to compare it with.',
  base_type_mismatch:
    'The base contract’s type was corrected after it was nominated, so it is no longer an MSA base. An administrator must nominate a new base.',
});

/** The 409 codes of a run, in the page's words. The readiness ones restate `readinessCopy`. */
export function runConflictCopy(code: BaseDeviationRunConflict): string {
  if (code === 'contract_changed') {
    return 'A contract changed while the report was being run, so nothing was stored. Reload and run it again.';
  }
  const side = code.startsWith('base_') ? 'base' : 'subject';
  const readiness = (side === 'base' ? code.slice('base_'.length) : code) as BaseDeviationSideReadiness;
  return readinessCopy(side, readiness) ?? '';
}

const RUN_CONFLICTS = new Set<string>([
  'not_converted',
  'not_segmented',
  'not_matched',
  'spans_stale',
  'base_not_converted',
  'base_not_segmented',
  'base_not_matched',
  'base_spans_stale',
  'contract_changed',
]);

/**
 * A failed Run as the section shows it: a named 409 or 400 in the page's own
 * words (never `ErrorNotice` above it — its copy for those kinds is written for
 * other surfaces), anything else by kind.
 */
export interface RunProblem {
  readonly kind: ApiProblemKind;
  readonly copy: string | null;
  readonly code: string | null;
  readonly correlation_id: string | null;
}

export function runProblemOf(error: unknown): RunProblem {
  const kind = classifyApiError(error);
  const code = refusalCodeOf(error);
  const correlation_id = error instanceof ApiError ? error.correlation_id : null;
  /* A code is trusted only with the status the API sends it under. */
  if (kind === 'conflict' && code !== null && RUN_CONFLICTS.has(code)) {
    return { kind, code, copy: runConflictCopy(code as BaseDeviationRunConflict), correlation_id };
  }
  if (kind === 'validation' && code !== null && (BASE_DEVIATION_SUBJECT_REFUSALS as readonly string[]).includes(code)) {
    return { kind, code, copy: SUBJECT_REFUSAL_COPY[code as BaseDeviationSubjectRefusal], correlation_id };
  }
  return { kind, code: null, copy: null, correlation_id };
}

/* -------------------------------------------------------------------------- */
/* The report page's vocabulary                                                */
/* -------------------------------------------------------------------------- */

/** The page's sections, in this order (spec §4.5): Modified, Different values, Different library version, Missing, Extra, then Unchanged. */
export const REPORT_SECTIONS: readonly {
  readonly id: string;
  readonly heading: string;
  readonly categories: readonly BaseDeviationCategory[];
  readonly collapsed: boolean;
}[] = Object.freeze([
  { id: 'modified', heading: 'Modified', categories: ['modified'], collapsed: false },
  { id: 'different_values', heading: 'Different values', categories: ['different_values'], collapsed: false },
  { id: 'different_library_version', heading: 'Different library version', categories: ['different_library_version'], collapsed: false },
  { id: 'missing', heading: 'Missing from their paper', categories: ['missing'], collapsed: false },
  { id: 'extra', heading: 'Extra — clauses they added', categories: ['added_library_clause', 'added_bespoke'], collapsed: false },
  { id: 'unchanged', heading: 'Unchanged', categories: ['unchanged'], collapsed: true },
]);

/** One-line explanations for each category of a line paired **through the library** (and the one-sided lines). */
export const CATEGORY_COPY: Readonly<Record<BaseDeviationCategory, string>> = Object.freeze({
  unchanged: 'The same clause on both sides, Standard against the library, with the same values.',
  modified: 'At least one side is Modified against the library clause.',
  different_values: 'Both sides are Standard against the library, but their values differ.',
  different_library_version: 'The same clause, matched to different versions of it in the library.',
  missing: 'The base has this clause; their paper does not.',
  added_library_clause: 'Their paper has a library clause the base does not.',
  added_bespoke: 'A clause of theirs that matches nothing in the library.',
});

/**
 * R-112-7's text-paired lines — `matched_by: 'fingerprint'`, only ever
 * `unchanged` or `different_values`. Neither paragraph is library-matched (the
 * pairing takes only rows outside the library, often unresolved ones), so
 * {@link CATEGORY_COPY}'s "Standard against the library" would be false on
 * them. The fingerprint is of the *normalised* wording — values and party
 * names collapse to tokens — so "the same wording", never "identical text":
 * the values are then compared separately, and may differ.
 */
export const FINGERPRINT_LINE_COPY: Readonly<Record<'unchanged' | 'different_values', { readonly heading: string; readonly copy: string }>> =
  Object.freeze({
    unchanged: {
      heading: 'The same wording, outside the library',
      copy: 'Paired by their text, not through a library clause: the same wording on both sides, with the same values.',
    },
    different_values: {
      heading: 'The same wording, outside the library, with different values',
      copy: 'Paired by their text, not through a library clause: the same wording on both sides, but their values differ.',
    },
  });

/** A code-less line's heading: a text pair, or a bespoke clause of theirs. `null` when the line names a clause code. */
export function codelessHeadingOf(line: BaseDeviationLine): string | null {
  if (line.clause_code !== null) return null;
  if (line.matched_by === 'fingerprint') {
    return line.category === 'different_values' ? FINGERPRINT_LINE_COPY.different_values.heading : FINGERPRINT_LINE_COPY.unchanged.heading;
  }
  return 'A clause outside the library';
}

/** The line's one-line explanation — never library copy on a line the library did not pair. */
export function lineCopyOf(line: BaseDeviationLine): string {
  if (line.matched_by === 'fingerprint') {
    return line.category === 'different_values' ? FINGERPRINT_LINE_COPY.different_values.copy : FINGERPRINT_LINE_COPY.unchanged.copy;
  }
  return CATEGORY_COPY[line.category];
}

/** Why something was not compared (NFR-18). */
export const NOT_COMPARED_COPY: Readonly<Record<NotComparedReason, string>> = Object.freeze({
  span_drift: 'its stored span no longer describes the contract’s current text',
  shortlist_rejected: 'the matcher’s shortlist was rejected — the paragraph is something else',
  not_matched_yet: 'the matcher has not reached it',
  awaiting_review: 'awaiting a reviewer’s decision',
  withheld_restricted: 'processing-restricted (§4C): no model may see it and no one has matched it',
  no_library_vectors: 'no library vector shares its embedding model',
  vector_distance_only: 'decided by vector distance alone, which is never a difference (NFR-18)',
  bespoke_unpaired: 'a base clause outside the library, with no identical paragraph in their paper',
  not_segmented_text: 'text no paragraph covers (held at segmentation)',
});

/** Where a quoted note came from (R-112-15). */
export const NOTE_SOURCE_COPY: Readonly<Record<BaseDeviationNoteSource, string>> = Object.freeze({
  human: 'a reviewer',
  deterministic: 'the value gate (no model)',
  model: 'the matching model',
});
